import { apiClient } from './apiClient';
import { mockTasks } from '@/mocks/data';
import type { Task, TaskStatus, PaginatedResponse } from '@/types';

const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true' || !import.meta.env.VITE_API_BASE_URL;

export interface TaskListParams {
  page?: number;
  pageSize?: number;
  status?: TaskStatus | 'ALL';
  priority?: string;
  assigneeId?: string;
  assignerId?: string;
  departmentId?: string;
  search?: string;
  tab?: 'assigned-to-me' | 'assigned-by-me' | 'watching' | 'collaborating' | 'created-by-me' | 'my-department' | 'archived' | 'all';
}

export interface CreateTaskPayload {
  title: string;
  description: string;
  assigneeIds: string[];
  watcherIds?: string[];
  dueDate: string;
  startDate?: string;
  priority: string;
  category: string;
  departmentId: string;
  tags?: string[];
  estimatedHours?: number;
  requiresSignature?: boolean;
  parentTaskId?: string;
  /** Project gán task vào (V1.0.23). NULL = task độc lập không thuộc project nào */
  projectId?: string;
  /** V1.0.25 (deprecated): giữ compat */
  urgency?: string;
  /** V1.0.26: trục GẤP cấp 1-5 */
  priorityLevel?: number;
  /** V1.0.26: trục QUAN TRỌNG cấp 1-5 */
  importantLevel?: number;
  /** V1.0.30: KPI doanh thu kế hoạch (VND). Null = không theo dõi KPI. */
  kpiRevenuePlan?: number | null;
}

export interface UpdateTaskPayload {
  title: string;
  description?: string;
  dueDate: string;
  startDate?: string;
  priority: string;
  category: string;
  departmentId: string;
  tags?: string[];
  estimatedHours?: number;
  priorityLevel?: number;
  importantLevel?: number;
  /** V1.0.30: KPI doanh thu kế hoạch (VND). Null/undefined = giữ nguyên. */
  kpiRevenuePlan?: number | null;
}

export interface RequestCloseTaskPayload {
  resultNote?: string;
  /** V1.0.30: KPI doanh thu thực tế (VND). Bắt buộc nếu root có plan. */
  kpiRevenueActual?: number | null;
}

export interface KpiTaskBreakdown {
  id: string;
  code: string;
  title: string;
  status: string;
  depth: number;
  parentTaskId?: string | null;
  assigneeName?: string | null;
  kpiRevenuePlan?: number | null;
  kpiRevenueActual?: number | null;
}

export interface KpiReportRow {
  id: string;
  code: string;
  title: string;
  status: string;
  dueDate: string;
  departmentName: string;
  assigneeName?: string | null;
  kpiRevenuePlan: number;
  aggregatedKpiActual: number;
  progressPercent: number;
  descendantCount: number;
  createdAt: string;
}

async function mockDelay<T>(data: T, ms = 300): Promise<T> {
  await new Promise((resolve) => setTimeout(resolve, ms));
  return data;
}

// Loại bỏ empty string, empty array element, undefined trước khi POST.
// Backend Guid không chấp nhận "", nên phải strip.
function cleanPayload(obj: any): any {
  const out: any = {};
  for (const [k, v] of Object.entries(obj ?? {})) {
    if (v === '' || v === undefined) continue;
    if (Array.isArray(v)) {
      const filtered = v.filter((item: any) => item !== '' && item !== null && item !== undefined);
      if (filtered.length === 0) continue; // skip empty array
      out[k] = filtered;
    } else {
      out[k] = v;
    }
  }
  return out;
}

export const taskService = {
  async list(params: TaskListParams = {}): Promise<PaginatedResponse<Task>> {
    if (USE_MOCK) {
      let items = [...mockTasks];
      if (params.status && params.status !== 'ALL') {
        items = items.filter((t) => t.status === params.status);
      }
      if (params.search) {
        const s = params.search.toLowerCase();
        items = items.filter(
          (t) => t.title.toLowerCase().includes(s) || t.code.toLowerCase().includes(s),
        );
      }
      return mockDelay({
        items,
        total: items.length,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 20,
      });
    }
    const { data } = await apiClient.get<PaginatedResponse<Task>>('/tasks', { params });
    return data;
  },

  async getById(id: string): Promise<Task> {
    if (USE_MOCK) {
      const task = mockTasks.find((t) => t.id === id);
      if (!task) throw new Error('Task không tồn tại');
      return mockDelay(task);
    }
    const { data } = await apiClient.get<Task>(`/tasks/${id}`);
    return data;
  },

  async create(payload: CreateTaskPayload): Promise<Task> {
    if (USE_MOCK) {
      const newTask: Task = {
        id: `task-${Date.now()}`,
        code: `TSK-${String(Math.floor(Math.random() * 9999)).padStart(4, '0')}`,
        ...payload,
        status: 'NEW',
        progress: 0,
        priority: payload.priority as Task['priority'],
        category: payload.category as Task['category'],
        urgency: (payload.urgency as Task['urgency']) ?? 'NORMAL',
        priorityLevel: (payload.priorityLevel as Task['priorityLevel']) ?? 3,
        importantLevel: (payload.importantLevel as Task['importantLevel']) ?? 3,
        department: mockTasks[0].department,
        assigner: mockTasks[0].assigner,
        assignees: [],
        watchers: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      return mockDelay(newTask);
    }
    const cleaned = cleanPayload(payload);
    const { data } = await apiClient.post<Task>('/tasks', cleaned);
    return data;
  },

  async update(id: string, payload: {
    title: string;
    description?: string;
    dueDate: string;
    startDate?: string;
    priority: string;
    category: string;
    departmentId: string;
    tags?: string[];
    estimatedHours?: number;
    priorityLevel?: number;
    importantLevel?: number;
    kpiRevenuePlan?: number;
    // V1.0.45: 4 mảng role — null = không sửa, array = sync với BE
    assigneeIds?: string[];
    watcherIds?: string[];
    collaboratorIds?: string[];
    informedIds?: string[];
  }): Promise<Task> {
    const { data } = await apiClient.put<Task>(`/tasks/${id}`, payload);
    return data;
  },

  async updateStatus(id: string, status: TaskStatus, reason?: string): Promise<Task> {
    const { data } = await apiClient.patch<Task>(`/tasks/${id}/status`, { status, reason });
    return data;
  },

  async updateProgress(id: string, progress: number, note?: string): Promise<Task> {
    const { data } = await apiClient.patch<Task>(`/tasks/${id}/progress`, { progress, note });
    return data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/tasks/${id}`);
  },

  async cancel(id: string): Promise<void> {
    await apiClient.post(`/tasks/${id}/cancel`);
  },

  async copy(id: string): Promise<Task> {
    const { data } = await apiClient.post<Task>(`/tasks/${id}/copy`);
    return data;
  },

  async toggleWatch(id: string): Promise<boolean> {
    const { data } = await apiClient.post<{ watching: boolean }>(`/tasks/${id}/watch`);
    return data.watching;
  },

  async addComment(taskId: string, content: string, mentionedUserIds: string[] = [], attachmentIds: string[] = []): Promise<void> {
    if (USE_MOCK) return mockDelay(undefined);
    await apiClient.post(`/tasks/${taskId}/comments`, { content, mentionedUserIds, attachmentIds });
  },

  async getComments(taskId: string): Promise<any[]> {
    const { data } = await apiClient.get<any[]>(`/tasks/${taskId}/comments`);
    return data;
  },

  async getHistory(taskId: string): Promise<any[]> {
    const { data } = await apiClient.get<any[]>(`/tasks/${taskId}/history`);
    return data;
  },

  async myCounts(): Promise<{
    assignedToMe: number;
    assignedByMe: number;
    watching: number;
    createdByMe: number;
    overdue: number;
    dueSoon: number;
    myDepartment?: number;  // V1.0.49: chỉ trả khi user là DEPT_HEAD
    archived?: number;       // V1.0.51: Task đã lưu trữ
    collaborating?: number;  // V1.0.63h: Tôi là COLLABORATOR (phối hợp)
  }> {
    const { data } = await apiClient.get('/tasks/my-counts');
    return data;
  },

  /** V1.0.25: upload file đính kèm task. Trả về AttachmentDto. */
  async uploadAttachment(taskId: string, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await apiClient.post(
      `/tasks/${taskId}/attachments`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data;
  },

  /**
   * V1.0.30: Đề nghị đóng task — chuyển sang PENDING_REVIEW.
   * Nếu root có KPI plan > 0 → kpiRevenueActual bắt buộc.
   */
  async requestClose(id: string, payload: RequestCloseTaskPayload): Promise<Task> {
    const { data } = await apiClient.post<Task>(`/tasks/${id}/request-close`, payload);
    return data;
  },

  /**
   * V1.0.30: Lấy breakdown KPI của task — cây con/cháu với plan/actual riêng.
   */
  async getKpiBreakdown(id: string): Promise<KpiTaskBreakdown[]> {
    const { data } = await apiClient.get<KpiTaskBreakdown[]>(`/tasks/${id}/kpi-breakdown`);
    return data;
  },
};

// ============================================================
// V1.0.30: KPI Report service
// ============================================================
export const kpiReportService = {
  async getReport(params: {
    departmentId?: string;
    assigneeUserId?: string;
    from?: string;
    to?: string;
  } = {}): Promise<KpiReportRow[]> {
    const clean: any = {};
    if (params.departmentId) clean.departmentId = params.departmentId;
    if (params.assigneeUserId) clean.assigneeUserId = params.assigneeUserId;
    if (params.from) clean.from = params.from;
    if (params.to) clean.to = params.to;
    const { data } = await apiClient.get<KpiReportRow[]>('/reports/kpi', { params: clean });
    return data;
  },
};
